import ApplicationModel, { attr } from 'components/framework/ApplicationModel';
import { type BookmarksCumulativeFlowDiagramAttributes } from 'components/framework/models/generated/BookmarksCumulativeFlowDiagramAttributes';
import { model } from 'components/framework/modelBuilder';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import DateRangeFilter from 'components/framework/filters/DateRangeFilter';
import RecordTypeFilter from 'components/framework/filters/RecordTypeFilter';
import WorkflowStatusFilter from 'components/framework/filters/WorkflowStatusFilter';
import type ItemFilter from 'components/framework/filters/ItemFilter';
import Project from 'components/framework/models/Project';
import Workflow from 'components/framework/models/Workflow';
import type WorkflowStatus from 'components/framework/models/WorkflowStatus';

type SupportedRecordType = 'Feature' | 'Requirement';

interface DateRange {
  start: string;
  end: string;
}

interface Local {
  filterObjects: Record<string, ItemFilter<any>>;
  workflowStatuses?: WorkflowStatus[];
}

interface Filters {
  dateRange: DateRange;
  recordType?: SupportedRecordType[];
  workflowStatus?: RecordId[];
}

interface BookmarksCumulativeFlowDiagram
  extends BookmarksCumulativeFlowDiagramAttributes,
    HasBookmarkFilters<{
      recordType: RecordTypeFilter;
      dateRange: DateRangeFilter;
      workflowStatus: WorkflowStatusFilter;
    }> {
  local: Local;
  filters: Filters;
}

@model
@HasBookmarkFilters
class BookmarksCumulativeFlowDiagram extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksCumulativeFlowDiagram' =
    'BookmarksCumulativeFlowDiagram';
  get typename(): 'BookmarksCumulativeFlowDiagram' {
    return 'BookmarksCumulativeFlowDiagram';
  }

  static internalTypename: 'Bookmarks::CumulativeFlowDiagram' =
    'Bookmarks::CumulativeFlowDiagram';
  get internalTypename(): 'Bookmarks::CumulativeFlowDiagram' {
    return 'Bookmarks::CumulativeFlowDiagram';
  }

  static bookmarkName = 'Bookmarks::CumulativeFlowDiagram';

  static inputType = 'CumulativeFlowDiagramAttributes!';

  static fields = {
    data: attr({ readable: true, json: true }),
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  initialize() {
    this.local = { filterObjects: {} };
    this.filters = this.filters || {};
  }

  /**
   * Initialize the filters
   */
  async createFilters() {
    const workflowStatuses = await this.availableWorkflowStatuses();

    return {
      recordType: new RecordTypeFilter({
        initialItems: this.filters.recordType ?? [],
        recordTypes: ['Feature', 'Requirement'],
      }),
      dateRange: new DateRangeFilter(this.filters.dateRange, false),
      workflowStatus: new WorkflowStatusFilter(
        this.filters.workflowStatus ?? [],
        workflowStatuses.map(s => [s.name, s.id]),
        {
          statusAttribute: 'teamWorkflowStatus',
        }
      ),
    };
  }

  canFilter(_scope: string) {
    return true;
  }

  async availableWorkflowStatuses(): Promise<WorkflowStatus[]> {
    if (typeof this.local.workflowStatuses === 'undefined') {
      const project = await Project.select('id')
        .merge({
          featureWorkflow: Workflow.select('id').merge({
            workflowStatuses: [
              'id',
              'name',
              'position',
              'color',
              'internalMeaning',
            ],
          }),
          requirementWorkflow: Workflow.select('id').merge({
            workflowStatuses: [
              'id',
              'name',
              'position',
              'color',
              'internalMeaning',
            ],
          }),
        })
        .find(this.projectId);

      const featureStatuses = project?.featureWorkflow.workflowStatuses ?? [];
      const requirementStatuses =
        project?.requirementWorkflow.workflowStatuses ?? [];

      const allStatuses = featureStatuses.concat(
        requirementStatuses.filter(
          status => !featureStatuses.find(s => s.id === status.id)
        )
      );

      this.local.workflowStatuses = allStatuses;
    }

    return this.local.workflowStatuses ?? [];
  }
}

export default BookmarksCumulativeFlowDiagram;
